import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { isObject } from 'lodash';
import { from } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { ImpersonationService } from '../services/impersonation.service';
import { ToastService } from '../services/toast.service';
import { statusOf } from '../services/vive-api-errors';
import { authService as viveAuthService } from '../shared/vive-api/auth.service';

declare const chmln: any;

@Component({
  selector: 'app-logout',
  templateUrl: './logout.component.html',
  styleUrls: ['./logout.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LogoutComponent implements OnInit {
  // The page text: this route runs a Return, not a sign-out, while impersonating.
  public returning = false;

  constructor(
    private Router: Router,
    private AuthService: AuthService,
    private ToastService: ToastService,
    private ImpersonationService: ImpersonationService
  ) {
    // Read while this page's navigation is still in progress; it is gone by
    // the time the revoke answers.
    const returnUrl =
      this.Router.currentNavigation()?.previousNavigation?.finalUrl?.toString() ??
      '/';

    // While impersonating, DELETE /auth/session returns to the staff session
    // too, but the sign-out below would then clear it from this tab and land on
    // login while the browser holds it.
    if (this.ImpersonationService.isImpersonating()) {
      this.returning = true;
      this.ImpersonationService.returnToMyAccount(returnUrl);
      return;
    }

    // Only a success or a 401 (the session had already ended) signs out. The
    // session is an HttpOnly cookie, so any other failure leaves it valid on the
    // server: clearing this tab would show the login form while a reload signs
    // the same user back in. The interceptor's toast is off: the 401 is not
    // an error here.
    from(
      viveAuthService.deleteSession({
        options: { context: { ManuallyHandleError: true } }
      })
    ).subscribe({
      next: () => this.performLogout(),
      error: (error: unknown) => {
        if (statusOf(error) === 401) {
          this.performLogout();
          return;
        }
        this.ToastService.addErrorToast(
          'Error',
          "We couldn't sign you out. Please try again."
        );
        this.Router.navigateByUrl(returnUrl);
      }
    });
  }

  private performLogout() {
    try {
      const User = this.AuthService.getUser();
      const { id: UserId } = User;

      const previousRoute =
        this.Router.currentNavigation()?.previousNavigation?.finalUrl?.toString();
      if (UserId && previousRoute) {
        const routingState = localStorage.getItem('routingState');
        let routingStateObject: { [key: number]: string } | undefined = {
          [UserId]: previousRoute
        };
        if (routingState) {
          try {
            const routingStateParsed = JSON.parse(routingState);
            routingStateObject = isObject(routingStateParsed)
              ? Object.assign(routingStateParsed, { [UserId]: previousRoute })
              : { [UserId]: previousRoute };
          } catch (error) {
            // Nothing to do.
          }
        }

        if (routingStateObject) {
          localStorage.setItem(
            'routingState',
            JSON.stringify(routingStateObject)
          );
        } else {
          localStorage.removeItem('routingState');
        }
      }

      this.clearLocalSession();
      localStorage.removeItem('isTermsAndConditionsConfirmed');
      this.redirectToLogin();
    } catch (error) {
      this.clearLocalSession();
      localStorage.removeItem('isTermsAndConditionsConfirmed');
      this.redirectToLogin();
    }
  }

  private clearLocalSession(): void {
    // Forgets the session and resets Datadog RUM and PostHog identity.
    this.AuthService.clearSession();
    // Reset Chameleon's session so the next user isn't seen as this one.
    try {
      chmln.clear();
    } catch (e) {
      console.error('Chameleon clear failed:', e);
    }
  }

  private redirectToLogin() {
    this.Router.navigateByUrl('login');
  }

  ngOnInit(): void {}
}
